import React, { useState } from 'react';
import { Form, Button, Alert, Card, Container } from 'react-bootstrap';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

const ExcelUploader = () => {
  const [jsonData, setJsonData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fileType, setFileType] = useState('');
  const [index, setIndex] = useState('');

  const expectedHeaders = [
    'token',
    'symbol',
    'name',
    'expiry',
    'strike',
    'lotsize',
    'instrumenttype',
    'exch_seg',
    'tick_size',
  ];

  const excelDateToJSDate = (serial) => {
    const utcDays = Math.floor(serial - 25569);
    const utcValue = utcDays * 86400;
    return new Date(utcValue * 1000);
  };

  const handleFileUpload = (e) => {
    if (!fileType || !index) {
      setError('Please enter index name and select file type (PE/CE) before uploading.');
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJson = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (rawJson.length === 0) {
          setError('The sheet is empty.');
          setJsonData(null);
          return;
        }

        const fileHeaders = Object.keys(rawJson[0]).map((header) => header.toLowerCase().trim());
        const headersValid =
          fileHeaders.length === expectedHeaders.length &&
          expectedHeaders.every((expHeader, index) => expHeader === fileHeaders[index]);

        if (!headersValid) {
          setError('Invalid header structure in uploaded file.');
          setJsonData(null);
          return;
        }

        const processedJson = rawJson.map((row) => {
          let expiryDate =
            typeof row.expiry === 'number'
              ? excelDateToJSDate(row.expiry)
              : new Date(row.expiry);
          const formattedExpiry = isNaN(expiryDate.getTime())
            ? row.expiry
            : format(expiryDate, 'dd-MMM-yy').toUpperCase();

          return {
            token: Number(row.token),
            symbol: row.symbol,
            name: row.name,
            expiry: formattedExpiry,
            strike: Number(row.strike),
            lotsize: Number(row.lotsize),
            instrumenttype: row.instrumenttype,
            exch_seg: row.exch_seg,
            tick_size: Number(row.tick_size),
          };
        });

        const filteredJson = processedJson.filter((row) =>
          fileType === 'ce'
            ? row.symbol.includes('CE')
            : row.symbol.includes('PE')
        );

        if (filteredJson.length === 0) {
          setError(`No ${fileType.toUpperCase()} options found for ${index}.`);
          setJsonData(null);
          return;
        }

        setJsonData(filteredJson);
        setError('');
      } catch (err) {
        setError(`Error processing file: ${err.message}`);
        setJsonData(null);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file.');
      setJsonData(null);
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadJSON = () => {
    if (!jsonData || !fileType || !index) {
      setError('Please ensure JSON data, file type, and index are set before downloading.');
      return;
    }
    const jsonString = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fileName = `${index.toLowerCase()}_${fileType.toLowerCase()}.json`;
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSuccess(`Downloaded ${fileName} successfully.`);
    setError('');
  };

  return (
    <Container className="shadow mt-4">
      <Card className="border-0 p-4">
        <h2 className="mb-3">Convert Excel to JSON</h2>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label>Enter Index Name:</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter index (e.g., NIFTY, BANKNIFTY)"
            value={index}
            onChange={(e) => setIndex(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Select Option Type:</Form.Label>
          <div className="d-flex gap-3">
            <Form.Check
              type="radio"
              label="CE"
              name="fileType"
              value="ce"
              checked={fileType === 'ce'}
              onChange={(e) => setFileType(e.target.value)}
            />
            <Form.Check
              type="radio"
              label="PE"
              name="fileType"
              value="pe"
              checked={fileType === 'pe'}
              onChange={(e) => setFileType(e.target.value)}
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Upload Excel File:</Form.Label>
          <Form.Control type="file" accept=".xls,.xlsx" onChange={handleFileUpload} />
        </Form.Group>

        {jsonData && (
          <div className="mt-4">
            <h5>Converted JSON Data ({jsonData.length} entries)</h5>
            <Button variant="primary" onClick={downloadJSON} className="mb-3">
              Download JSON
            </Button>
            <pre className="p-3 border rounded bg-light">
              {JSON.stringify(jsonData.slice(0, 2), null, 2)}
              {jsonData.length > 2 && '...'}
            </pre>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default ExcelUploader;